// Progressive-enhancement carousel controls for the shoppable-testimonials section.
// The track is a native scroll-snap band: mobile swipe and peek work with zero JS. This
// custom element adds desktop-only prev/next arrows that scroll one card at a time.
//
// End-state contract (mirrors the plan): arrows are hidden (and removed from the a11y tree)
// when the whole track fits; when it overflows, both arrows stay present and the end-most one
// is disabled at each end. Arrows are additionally hidden on mobile via CSS. With no JS, the
// buttons keep their initial `hidden` attribute and the band still swipes.
class ShoppableTestimonials extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.shoppable-testimonials__track');
    if (!this.track) return;

    this.prev = this.querySelector('[data-dir="prev"]');
    this.next = this.querySelector('[data-dir="next"]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
    if (this.next) this.next.addEventListener('click', () => this.step(1));

    this.onScroll = () => this.update();
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
    }

    this.update();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
  }

  cardStep() {
    const slide = this.track.querySelector('.shoppable-testimonials__slide');
    if (!slide) return this.track.clientWidth;
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  step(direction) {
    this.track.scrollBy({
      left: direction * this.cardStep(),
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });
  }

  update() {
    const fits = this.track.scrollWidth <= this.track.clientWidth + 1;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;

    if (this.prev) {
      this.prev.hidden = fits;
      this.prev.disabled = atStart;
    }
    if (this.next) {
      this.next.hidden = fits;
      this.next.disabled = atEnd;
    }
  }
}

if (!customElements.get('shoppable-testimonials')) {
  customElements.define('shoppable-testimonials', ShoppableTestimonials);
}
